import BaseButton from '@/components/base/BaseButton';
import BaseLoading from '@/components/base/BaseLoading';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ConfigProvider, Modal, type ModalProps } from 'antd';
import enUS from 'antd/locale/en_US';
import { useContext, type MouseEvent, type ReactNode } from 'react';

export interface BaseModalProps
  extends Omit<ModalProps, 'title' | 'footer' | 'onCancel'> {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  footer?: ModalProps['footer'];
  onClose: ModalProps['onCancel'];
  icon?: ReactNode;
  /** 保留正文挂载与自然尺寸，等待期间统一遮罩、禁用内容。 */
  contentLoading?: boolean;
  loadingLabel?: string;
}

/** 统一弹窗的居中、销毁与内容滚动，内容和操作由调用方提供。 */
export default function BaseModal({
  open,
  title,
  children,
  footer,
  onClose,
  width = 760,
  icon,
  contentLoading,
  loadingLabel = '',
  className,
  classNames,
  onOk,
  okText,
  cancelText,
  okType = 'primary',
  confirmLoading,
  okButtonProps,
  cancelButtonProps,
  ...props
}: BaseModalProps) {
  const { locale } = useContext(ConfigProvider.ConfigContext);
  const defaultFooter = (
    <>
      <BaseButton
        icon={<CloseOutlined aria-hidden />}
        onClick={(event) => onClose?.(event as MouseEvent<HTMLButtonElement>)}
        {...cancelButtonProps}
      >
        {cancelText || locale?.Modal?.cancelText || enUS.Modal?.cancelText}
      </BaseButton>
      <BaseButton
        type={okType === 'danger' ? 'primary' : okType}
        danger={okType === 'danger'}
        icon={<CheckOutlined aria-hidden />}
        loading={confirmLoading}
        onClick={(event) => onOk?.(event as MouseEvent<HTMLButtonElement>)}
        {...okButtonProps}
      >
        {okText || locale?.Modal?.okText || enUS.Modal?.okText}
      </BaseButton>
    </>
  );
  return (
    <Modal
      {...props}
      open={open}
      title={
        <div className="common-modal-heading flex min-h-8.5 items-center gap-3 pr-7.5 text-text text-[16px] font-semibold leading-[24px]">
          {icon && (
            <span
              className="common-modal-icon flex w-8.5 h-8.5 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary"
              aria-hidden
            >
              {icon}
            </span>
          )}
          <span>{title}</span>
        </div>
      }
      footer={footer === undefined ? defaultFooter : footer}
      onOk={onOk}
      onCancel={onClose}
      okText={okText}
      cancelText={cancelText}
      okType={okType}
      confirmLoading={confirmLoading}
      okButtonProps={okButtonProps}
      cancelButtonProps={cancelButtonProps}
      width={width}
      centered
      destroyOnHidden
      className={[
        'common-modal max-w-[calc(100vw_-_48px)] pb-0 max-[641px]:max-w-[calc(100vw_-_24px)]',
        contentLoading !== undefined && 'common-modal-async',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      classNames={classNames ?? { mask: 'common-modal-mask' }}
    >
      {contentLoading === undefined ? (
        children
      ) : (
        <>
          <div
            className={contentLoading ? 'invisible' : undefined}
            aria-hidden={contentLoading || undefined}
            inert={contentLoading}
          >
            {children}
          </div>
          {contentLoading && (
            <BaseLoading overlay centerInVisibleArea label={loadingLabel} />
          )}
        </>
      )}
    </Modal>
  );
}
